import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LayoutGrid, List, Plus, Pencil, Trash2, Briefcase } from 'lucide-react';
import { PERMISSIONS, type AdditionalTask } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type Column } from '@/components/ui/data-table';
import { DateRangePicker } from '@/components/tasks/DateRangePicker';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { additionalTasksService } from '@/services/additional-tasks.service';
import { usersService } from '@/services/users.service';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { AdditionalWorkModal } from '@/components/tasks/AdditionalWorkModal';
import { AdditionalWorkCard } from '@/components/tasks/AdditionalWorkCard';
import { AdditionalWorkDetailModal } from '@/components/tasks/AdditionalWorkDetailModal';
import { fullName } from '@/components/tasks/taskConstants';

const PAGE_LIMIT = 24;

function minutesToLabel(mins: number): string {
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function AdditionalTaskPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { has } = usePermission();
  const canCreate = has(PERMISSIONS.ADDITIONAL_TASK_CREATE);
  const canManageAll = has(PERMISSIONS.ADDITIONAL_TASK_MANAGE);

  const [view, setView] = useState<'grid' | 'table'>('grid');
  const [assignedBy, setAssignedBy] = useState('all');
  const [createdBy, setCreatedBy] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdditionalTask | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdditionalTask | null>(null);
  const [viewTask, setViewTask] = useState<AdditionalTask | null>(null);

  const usersQ = useQuery({
    queryKey: ['tasks', 'assignee-picker'],
    queryFn: () => usersService.picker(),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['additional-tasks', { assignedBy, createdBy, dateFrom, dateTo, page }],
    queryFn: () => additionalTasksService.list({
      assigned_by: assignedBy === 'all' ? undefined : assignedBy,
      created_by: createdBy === 'all' ? undefined : createdBy,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      limit: PAGE_LIMIT,
    }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => additionalTasksService.remove(id),
    onSuccess: () => {
      toast.success('Additional work deleted');
      qc.invalidateQueries({ queryKey: ['additional-tasks'] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }
  function canManageRow(item: AdditionalTask): boolean {
    return canManageAll || item.user_id === user?.id;
  }
  function openEdit(item: AdditionalTask) { setEditing(item); setModalOpen(true); }
  function openCreate() { setEditing(null); setModalOpen(true); }

  const rows = data?.data ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;
  const total = data?.pagination.total ?? 0;
  const users = usersQ.data ?? [];

  const columns: Column<AdditionalTask>[] = [
    { key: 'title', header: 'Title', cell: (r) => <span className="font-medium">{r.title}</span> },
    { key: 'date', header: 'Date', cell: (r) => new Date(r.work_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) },
    { key: 'time', header: 'Time', cell: (r) => `${r.start_time.slice(0, 5)}–${r.end_time.slice(0, 5)}` },
    { key: 'duration', header: 'Duration', cell: (r) => <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{minutesToLabel(r.duration_minutes)}</span> },
    { key: 'employee', header: 'Employee', cell: (r) => <span>{fullName(r.first_name, r.last_name)}{r.employee_code ? ` (${r.employee_code})` : ''}</span> },
    { key: 'assigned_by', header: 'Assigned By', cell: (r) => r.assigned_by_first_name ? fullName(r.assigned_by_first_name, r.assigned_by_last_name) : '—' },
    {
      key: 'actions', header: '', className: 'w-20 text-right',
      cell: (r) => canManageRow(r) ? (
        <div className="flex justify-end gap-1">
          <button type="button" onClick={() => openEdit(r)} title="Edit" className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={() => setDeleteTarget(r)} title="Delete" className="rounded-md p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
        </div>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Additional Work</h1>
          <p className="text-sm text-muted-foreground">Track and manage additional work beyond regular tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-border/60 p-0.5">
            <button type="button" onClick={() => setView('grid')} title="Grid view"
              className={cn('rounded-md p-1.5', view === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/50')}>
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setView('table')} title="Table view"
              className={cn('rounded-md p-1.5', view === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/50')}>
              <List className="h-4 w-4" />
            </button>
          </div>
          {canCreate && (
            <Button onClick={openCreate}><Plus className="h-4 w-4" /> Add Work</Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 lg:flex-row lg:items-center">
        <Select value={assignedBy} onValueChange={resetPage(setAssignedBy)}>
          <SelectTrigger className="lg:w-52"><SelectValue placeholder="All Assigned By" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assigned By</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{fullName(u.first_name, u.last_name)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={createdBy} onValueChange={resetPage(setCreatedBy)}>
          <SelectTrigger className="lg:w-52"><SelectValue placeholder="All Created By" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Created By</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{fullName(u.first_name, u.last_name)}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={resetPage(setDateFrom)}
          onDateToChange={resetPage(setDateTo)}
          className="lg:w-64"
        />
      </div>

      {/* Content */}
      {view === 'grid' ? (
        <div className="space-y-4">
          {isLoading && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-48 animate-pulse rounded-xl border border-border/60 bg-muted/40" />)}
            </div>
          )}
          {isError && (
            <div className="rounded-xl border border-border/60 p-10 text-center text-sm text-muted-foreground">
              Couldn’t load additional work. <button className="text-primary underline" onClick={() => refetch()}>Retry</button>
            </div>
          )}
          {data && rows.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border/60 p-12 text-center text-sm text-muted-foreground">
              <Briefcase className="h-8 w-8 opacity-40" />
              No additional work found.
              {canCreate && <Button variant="outline" size="sm" onClick={openCreate}>Add Work</Button>}
            </div>
          )}
          {rows.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((item) => (
                <AdditionalWorkCard
                  key={item.id}
                  item={item}
                  canManage={canManageRow(item)}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                  onView={setViewTask}
                />
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{total} total · Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={isLoading}
          error={isError}
          onRetry={refetch}
          empty="No additional work found."
          pagination={{ page, totalPages, total, onPageChange: setPage }}
        />
      )}

      <AdditionalWorkModal open={modalOpen} onOpenChange={setModalOpen} task={editing} />

      <AdditionalWorkDetailModal
        task={viewTask}
        open={!!viewTask}
        onOpenChange={(o) => { if (!o) setViewTask(null); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete additional work?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.title}” will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
